import React, { useState } from "react";
import { z } from "zod";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CalendarPlus } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
import { errMsg } from "./calendarUtils";
import { ServiceCheckboxGroup } from "./ServiceCheckboxGroup";
import useFieldErrors from "../../hooks/useFieldErrors";
import FieldError, { invalidInputClass } from "../../components/ui/FieldError";

// Mirrors `ExtraHoursCreate.reason` (modules/booking/schemas.py).
const MAX_REASON_LEN = 200;

// EXTRA HOURS: opening hours on ONE date outside the weekly grid
// (POST /admin/extra-hours, stored as an exception of kind "open"). The start times inside
// them are computed exactly like a weekly window's — each service's own length,
// back-to-back, then the buffer.
//
// The services are REQUIRED here, unlike on a weekly window: there is no template default
// to inherit on a date the grid does not cover, and hours offering no service have no
// length to offer (the server answers 422 on an empty set). "HH:MM" strings compare in
// time order, which is what lets the end-after-start rule be a string comparison.
const EXTRA_HOURS_SCHEMA = z.object({
  date: z.string().min(1, "ovr.extraDateRequired"),
  start: z.string().min(1, "ovr.extraTimesRequired"),
  end: z.string().min(1, "ovr.extraTimesRequired"),
  service_ids: z.array(z.string()).min(1, "ovr.extraServicesRequired"),
  reason: z.string().max(MAX_REASON_LEN, "ovr.extraReasonTooLong"),
}).superRefine((form, ctx) => {
  if (form.start && form.end && form.end <= form.start) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["end"], message: "sched.windowInvalid" });
  }
});

export const ExtraHoursDialog = ({ services, onClose, onSaved }) => {
  const t = useT("admin");
  const tc = useT("common");
  const [form, setForm] = useState({ date: "", start: "09:00", end: "12:00", service_ids: [], reason: "" });
  const [saving, setSaving] = useState(false);
  const { fieldErrors, validate, clearFieldError } = useFieldErrors();

  const updateField = (k, v) => { setForm((f) => ({ ...f, [k]: v })); clearFieldError(k); };

  const save = async () => {
    if (validate(EXTRA_HOURS_SCHEMA, form)) { toast.error(tc("validation.fixHighlighted")); return; }
    setSaving(true);
    try {
      await api.post("/admin/extra-hours", {
        date: form.date,
        start: form.start,
        end: form.end,
        service_ids: form.service_ids,
        ...(form.reason.trim() ? { reason: form.reason.trim() } : {}),
      });
      toast.success(t("ovr.extraCreated"));
      onSaved && onSaved();
      onClose();
    } catch (e) {
      // On this door INVALID_WINDOWS has one meaning the generic sentence does not say:
      // the hours overlap that date's weekly hours or other extra hours already set.
      const detail = e?.response?.data?.detail;
      toast.error(detail === "INVALID_WINDOWS" ? t("ovr.extraOverlap") : errMsg(e, t));
    } finally {
      setSaving(false);
    }
  };

  const fieldMsg = (k) => (fieldErrors[k] ? t(fieldErrors[k], { max: MAX_REASON_LEN }) : "");

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="ovr-extra-dialog">
        <DialogHeader>
          <DialogTitle>{t("ovr.extraDialogTitle")}</DialogTitle>
          <DialogDescription>{t("ovr.extraDialogDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">{t("ovr.extraDate")}</Label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => updateField("date", e.target.value)}
              className={`mt-1 ${fieldErrors.date ? invalidInputClass : ""}`}
              data-testid="ovr-extra-date"
            />
            <FieldError message={fieldMsg("date")} testId="ovr-extra-date-error" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">{t("ovr.extraStart")}</Label>
              <Input
                type="time"
                value={form.start}
                onChange={(e) => updateField("start", e.target.value)}
                className={`mt-1 ${fieldErrors.start ? invalidInputClass : ""}`}
                data-testid="ovr-extra-start"
              />
              <FieldError message={fieldMsg("start")} testId="ovr-extra-start-error" />
            </div>
            <div>
              <Label className="text-xs">{t("ovr.extraEnd")}</Label>
              <Input
                type="time"
                value={form.end}
                onChange={(e) => updateField("end", e.target.value)}
                className={`mt-1 ${fieldErrors.end ? invalidInputClass : ""}`}
                data-testid="ovr-extra-end"
              />
              <FieldError message={fieldMsg("end")} testId="ovr-extra-end-error" />
            </div>
          </div>
          <div>
            <Label className="text-xs">{t("ovr.extraServices")}</Label>
            {services.length > 0 ? (
              <div className="mt-1">
                <ServiceCheckboxGroup
                  services={services}
                  selectedIds={form.service_ids}
                  onChange={(ids) => updateField("service_ids", ids)}
                  idPrefix="ovr-extra-service"
                />
              </div>
            ) : (
              <p className="text-[11px] text-eh-muted mt-1">{t("sched.noServices")}</p>
            )}
            <FieldError message={fieldMsg("service_ids")} testId="ovr-extra-services-error" />
          </div>
          <div>
            <Label className="text-xs">{t("ovr.reason")}</Label>
            <Input
              value={form.reason}
              onChange={(e) => updateField("reason", e.target.value)}
              placeholder={t("ovr.extraReasonPlaceholder")}
              className={`mt-1 ${fieldErrors.reason ? invalidInputClass : ""}`}
              data-testid="ovr-extra-reason"
            />
            <FieldError message={fieldMsg("reason")} testId="ovr-extra-reason-error" />
          </div>
          <Button onClick={save} disabled={saving} className="w-full bg-eh-ink text-eh-bg hover:opacity-90" data-testid="ovr-extra-save-btn">
            {saving ? "…" : <><CalendarPlus className="w-4 h-4 mr-1" /> {t("ovr.extraCreate")}</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
